import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@roger/ui-components';

import {
  getPatientChartImportReportPatientsPatientIdChartImportsRunIdReportGet as getPatientChartImportReport,
  listPatientImportReviewItemsPatientsPatientIdChartImportsReviewItemsGet as listPatientImportReviewItems,
  markPatientChartImportReviewedPatientsPatientIdChartImportsRunIdReviewPost as markPatientChartImportReviewed,
} from '../../services/api/sdk.gen';
import type {
  ChartImportReportResponse,
  ChartImportRunSummary,
  ImportReviewItemRead,
  ReportLine,
  ReportSection,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { formatDateInPT } from '../../utils/dateUtils';
import {
  IMPORT_REPORT_EMPTY_COPY,
  IMPORT_REPORT_ERROR_COPY,
  IMPORT_REPORT_LABEL,
  MARK_REVIEWED_ERROR_COPY,
  MARK_REVIEWED_LABEL,
  SEVERITY_TITLES,
  reviewedByLine,
  runChipLabel,
  runSummaryLine,
  sectionCollapsedLabel,
  showItemsLabel,
  singleRunLabel,
} from './importReportCopy';
import ImportReviewItems from './ImportReviewItems';
import KinnserLink from './KinnserLink';
import { REVIEW_ITEMS_ERROR_COPY } from './importReviewCopy';

interface ImportReportDialogProps {
  patientId: string;
  /** Every recorded run for this chart, newest first. */
  runs: ChartImportRunSummary[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Hands the caller a new run list after a run is marked reviewed. */
  onRunsChanged: (runs: ChartImportRunSummary[]) => void;
  /** Hands the caller the open review-item count after it changes. */
  onReviewItemsOpenChanged?: (openCount: number) => void;
}

/** Sections a reader must act on stay open; the rest fold away. */
const ALWAYS_OPEN_SECTIONS = new Set(['action', 'loss']);

/**
 * The review items as loaded for one chart in one opening of the dialog.
 *
 * Held with the chart they belong to, and dropped when the dialog closes, so
 * nothing from a previous chart or a previous opening is ever on screen: an
 * item a refresh updated in the meantime keeps its id but not its values,
 * and "Take External EMR's" must only ever act on values the reader saw.
 */
interface LoadedReviewItems {
  patientId: string;
  items: ImportReviewItemRead[];
  canAcknowledge: boolean;
}

/**
 * The run the reader most likely came for.
 *
 * The header button counts the newest unreviewed run, so opening to anything
 * else would answer a question the reader did not ask. With every run reviewed
 * the newest run is the one still worth reading.
 */
function defaultRunId(runs: ChartImportRunSummary[]): string | null {
  if (runs.length === 0) return null;
  const unreviewed = runs.filter(
    run => !run.reviewed_at && run.verb !== 'refresh_preview' && !run.scheduled
  );
  return (unreviewed[0] ?? runs[0]).id;
}

function totalFindings(section: ReportSection): number {
  return (section.lines ?? []).reduce(
    (sum, line) => sum + (line.count || 1),
    0
  );
}

/**
 * One finding, or one family of findings collapsed to a single sentence.
 *
 * The subjects are already inside the message the backend wrote, so this
 * renders the sentence and nothing else. When a family holds more than one
 * finding, the reader can open the individual lines and their Kinnser links.
 */
const ReportLineRow: React.FC<{ line: ReportLine }> = ({ line }) => {
  const [expanded, setExpanded] = useState(false);
  const items = line.items ?? [];
  const count = line.count ?? 1;

  return (
    <li className='flex gap-2' data-testid='import-report-line'>
      {/* Two severities, two different jobs: one thing never arrived, the
          other arrived and needs a second pair of eyes. */}
      <span
        className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
          line.severity === 'not_applied' ? 'bg-destructive' : 'bg-amber-500'
        }`}
        data-severity={line.severity}
        title={SEVERITY_TITLES[line.severity]}
      />
      <div className='min-w-0 flex-1 space-y-1'>
        <p className='text-sm'>{line.message}</p>
        <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
          {line.url && <KinnserLink url={line.url} />}
          {count > 1 && items.length > 0 && (
            <Button
              variant='ghost'
              size='sm'
              aria-expanded={expanded}
              onClick={() => setExpanded(open => !open)}
            >
              {showItemsLabel(count, expanded)}
            </Button>
          )}
        </div>
        {expanded && items.length > 0 && (
          <ul
            className='space-y-1 border-l pl-3'
            data-testid='import-report-items'
          >
            {items.map((item, index) => (
              <li key={`${item.message}-${index}`} className='space-y-0.5'>
                <p className='text-sm text-muted-foreground'>{item.message}</p>
                {item.url && <KinnserLink url={item.url} />}
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
};

const ReportLineList: React.FC<{ lines: ReportLine[] }> = ({ lines }) => (
  <ul className='space-y-2'>
    {lines.map((line, index) => (
      <ReportLineRow
        key={`${line.kind}-${line.section}-${index}`}
        line={line}
      />
    ))}
  </ul>
);

/**
 * A section of the report.
 *
 * "To do in Roger" and "Did not come across" are the reader's work, so they
 * are open on arrival. "Roger does this differently" and "Technical" are
 * context — present, counted, and out of the way until asked for.
 */
const ReportSectionBlock: React.FC<{ section: ReportSection }> = ({
  section,
}) => {
  const lines = section.lines ?? [];
  if (lines.length === 0) return null;

  if (ALWAYS_OPEN_SECTIONS.has(section.kind)) {
    return (
      <section className='space-y-2' data-testid={`section-${section.kind}`}>
        <h3 className='text-sm font-semibold'>{section.label}</h3>
        <ReportLineList lines={lines} />
      </section>
    );
  }

  return (
    <Collapsible className='space-y-2' data-testid={`section-${section.kind}`}>
      <CollapsibleTrigger className='flex w-full items-center gap-1 text-left text-sm font-semibold'>
        <ChevronDown className='h-3.5 w-3.5 shrink-0' aria-hidden='true' />
        {sectionCollapsedLabel(section.label, totalFindings(section))}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ReportLineList lines={lines} />
      </CollapsibleContent>
    </Collapsible>
  );
};

/**
 * What one Kinnser import did to this chart, in the reader's own terms.
 *
 * The report is grouped by what the reader does with each line rather than by
 * where the data lives, so the first thing on screen is the work waiting in
 * Roger. Marking it reviewed is the record that a person read it — the count
 * on the header button is what that record clears.
 */
const ImportReportDialog: React.FC<ImportReportDialogProps> = ({
  patientId,
  runs,
  open,
  onOpenChange,
  onRunsChanged,
  onReviewItemsOpenChanged,
}) => {
  const showError = useStore(state => state.showError);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [loadedReviewItems, setLoadedReviewItems] =
    useState<LoadedReviewItems | null>(null);
  const [report, setReport] = useState<ChartImportReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [marking, setMarking] = useState(false);
  const [justReviewed, setJustReviewed] =
    useState<ChartImportRunSummary | null>(null);
  // Which opening of the dialog this is. It moves each time the dialog
  // closes, so a review-item callback made for one opening can tell, when its
  // request finally settles, that the reader has since closed and reopened.
  const openingRef = useRef(0);
  const [opening, setOpening] = useState(0);
  useEffect(() => {
    if (open) return;
    openingRef.current += 1;
    setOpening(openingRef.current);
  }, [open]);

  // The picker resets each time the dialog opens: a reader returning to it
  // after an import expects the newest run, not wherever they last browsed.
  useEffect(() => {
    // Deliberately keyed on `open` alone: a run list refreshed underneath the
    // reader must not move the report out from under them.
    if (open) setSelectedRunId(defaultRunId(runs));
  }, [open]);

  // The review items are the chart's, not one run's. Loaded once per opening,
  // settled ones included. Nothing is shown until this opening's list arrives
  // or after a failed load: a cached list could offer "Take External EMR's"
  // against values the server has since replaced.
  useEffect(() => {
    if (!open) {
      setLoadedReviewItems(null);
      return;
    }
    let cancelled = false;

    const loadItems = async () => {
      try {
        const { data } = await listPatientImportReviewItems({
          path: { patient_id: patientId },
          query: { include_acknowledged: true },
          throwOnError: true,
        });
        if (!cancelled && data) {
          const items = data.items ?? [];
          setLoadedReviewItems({
            patientId,
            items,
            canAcknowledge: Boolean(data.can_acknowledge),
          });
          onReviewItemsOpenChanged?.(
            data.open_count ??
              items.filter(item => !item.acknowledged_at).length
          );
        }
      } catch (err) {
        console.error('Failed to load the import review items', err);
        if (!cancelled) showError(REVIEW_ITEMS_ERROR_COPY);
      }
    };

    void loadItems();
    return () => {
      cancelled = true;
    };
  }, [open, patientId, onReviewItemsOpenChanged, showError]);

  // Only a list loaded for this chart is this chart's. A list for another
  // patient, still in state while its replacement loads, is not.
  const reviewItemsForChart =
    loadedReviewItems?.patientId === patientId ? loadedReviewItems : null;

  // Bound to the opening that made it. An acknowledge-all or a 409 read-back
  // can still be pending when the reader closes and reopens the report; its
  // rows and count are older than the new opening's list, so they are
  // dropped, header count included.
  const handleReviewItemsChanged = useCallback(
    (items: ImportReviewItemRead[]) => {
      if (openingRef.current !== opening) return;
      setLoadedReviewItems(loaded => (loaded ? { ...loaded, items } : loaded));
      onReviewItemsOpenChanged?.(
        items.filter(item => !item.acknowledged_at).length
      );
    },
    [onReviewItemsOpenChanged, opening]
  );

  useEffect(() => {
    if (!open || !selectedRunId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setFailed(false);
      try {
        const { data } = await getPatientChartImportReport({
          path: { patient_id: patientId, run_id: selectedRunId },
          throwOnError: true,
        });
        if (!cancelled) setReport(data ?? null);
      } catch (err) {
        console.error('Failed to load the chart import report', err);
        if (!cancelled) {
          setReport(null);
          setFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [open, patientId, selectedRunId]);

  // Three copies of the same run, newest first. A review recorded here is
  // newer than anything fetched. The report the server just returned is newer
  // than the list the caller loaded earlier, so a colleague who reviewed this
  // run in the meantime shows up rather than being overwritten by a stale row.
  const listedRun = runs.find(run => run.id === selectedRunId) ?? null;
  const loadedRun =
    report && report.run.id === selectedRunId ? report.run : null;
  const selectedRun =
    (justReviewed?.id === selectedRunId ? justReviewed : null) ??
    loadedRun ??
    listedRun ??
    report?.run ??
    null;

  // Nobody signs off a report they have not been shown. Until this run's
  // report is on screen there is nothing to have read, so the button holds.
  const reportIsOnScreen = !loading && !failed && loadedRun !== null;

  const markReviewed = useCallback(async () => {
    if (!selectedRun) return;
    setMarking(true);
    try {
      const { data } = await markPatientChartImportReviewed({
        path: { patient_id: patientId, run_id: selectedRun.id },
        throwOnError: true,
      });
      if (data) {
        setJustReviewed(data);
        onRunsChanged(runs.map(run => (run.id === data.id ? data : run)));
      }
    } catch (err) {
      console.error('Failed to mark the chart import report reviewed', err);
      showError(getBusinessErrorMessage(err) ?? MARK_REVIEWED_ERROR_COPY);
    } finally {
      setMarking(false);
    }
  }, [onRunsChanged, patientId, runs, selectedRun, showError]);

  const sections = report?.sections ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{IMPORT_REPORT_LABEL}</DialogTitle>
        </DialogHeader>

        {runs.length > 1 ? (
          <div
            className='flex flex-wrap items-center gap-1'
            data-testid='import-report-run-picker'
          >
            {runs.map(run => (
              <Button
                key={run.id}
                size='sm'
                variant={run.id === selectedRunId ? 'secondary' : 'ghost'}
                onClick={() => setSelectedRunId(run.id)}
              >
                <span className='whitespace-nowrap'>
                  {runChipLabel(run.verb, formatDateInPT(run.imported_at))}
                </span>
              </Button>
            ))}
          </div>
        ) : (
          selectedRun && (
            <p className='text-sm text-muted-foreground'>
              {singleRunLabel(formatDateInPT(selectedRun.imported_at))}
            </p>
          )
        )}

        {/* Also the dialog's accessible description: the counts are the
            shortest true answer to "what did this import do?". */}
        <DialogDescription
          className='text-sm font-medium text-foreground'
          data-testid='import-report-summary'
        >
          {selectedRun ? runSummaryLine(selectedRun.counts) : ''}
        </DialogDescription>

        <div className='space-y-4'>
          <ImportReviewItems
            patientId={patientId}
            items={reviewItemsForChart?.items ?? []}
            canAcknowledge={reviewItemsForChart?.canAcknowledge ?? false}
            onItemsChanged={handleReviewItemsChanged}
          />
          {loading && (
            <div className='space-y-2' data-testid='import-report-loading'>
              <Skeleton className='h-4 w-2/3' />
              <Skeleton className='h-4 w-1/2' />
              <Skeleton className='h-4 w-3/4' />
            </div>
          )}
          {!loading && failed && (
            <p className='text-sm' data-testid='import-report-error'>
              {IMPORT_REPORT_ERROR_COPY}
            </p>
          )}
          {!loading && !failed && report?.is_empty && (
            <p className='text-sm' data-testid='import-report-empty'>
              {IMPORT_REPORT_EMPTY_COPY}
            </p>
          )}
          {!loading &&
            !failed &&
            !report?.is_empty &&
            sections.map(section => (
              <ReportSectionBlock key={section.kind} section={section} />
            ))}
        </div>

        <DialogFooter>
          {selectedRun?.reviewed_at ? (
            <p
              className='text-sm text-muted-foreground'
              data-testid='import-report-reviewed'
            >
              {reviewedByLine(
                selectedRun.reviewed_by_name,
                formatDateInPT(selectedRun.reviewed_at)
              )}
            </p>
          ) : (
            selectedRun?.can_mark_reviewed &&
            selectedRun.verb !== 'refresh_preview' && (
              <Button
                size='sm'
                disabled={marking || !reportIsOnScreen}
                onClick={() => void markReviewed()}
              >
                {MARK_REVIEWED_LABEL}
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportReportDialog;
